/*
  <type-banners>
  Renders the LivGood type banners that match the collection currently on screen.

  API: GET {api_base}/active?platform=web
  Response:
    { "success": true, "results": 3,
      "data":   [ { id, type, typeLabel, banner, title, subtitle, collectionId,
                    collectionHandle, collectionTitle, link, tappable, position } ],
      "byType": { "snacks": [ ... ], "supplements": [ ... ] } }

  Where banners appear
  --------------------
  Two places, in this order:

    1. Above each product-type shelf. This theme's collection grid splits the
       products into one .product-type-row per product type, each with its own
       heading. A "Protein Bars" banner belongs above that shelf — which is how
       it reaches a mixed collection like /collections/snacks, where the
       collection itself is not a type but the shelves inside it are.

    2. In the section's own slot near the top, for a collection page that is
       itself a single type, like /collections/protein-bars.

  A banner already placed on a shelf is skipped in the second pass, so the same
  artwork never appears twice on one page.

  Matching
  --------
  A banner's `type` is either a Shopify product_type ("snacks") or a slot key
  the front end owns ("home_top"). The collection page offers several possible
  names for itself, tried in this order (first one with banners wins):

    1. the section's "Type override" setting
    2. the collection metafield custom.banner_type
    3. the collection's product types (collection.all_types), but only when the
       collection has few enough of them to actually be a "type" shelf — a big
       mixed collection like /collections/all would otherwise match a banner
       belonging to whichever type happened to come first
    4. the collection handle
    5. the collection title

  Each candidate is compared against both `type` AND `typeLabel`, raw-lowercase
  and handleised ("Snacks & Bars" -> "snacks-bars"), so a collection whose
  handle is `snacks-bars` still matches a banner whose type is `snacks` with
  typeLabel "Snacks & Bars".

  Rules from the API brief:
    - Check `tappable` before making the banner a link. When false, render a
      plain <img> with no anchor.
    - Use `link` for the href — but only when it is actually a web url. The
      same admin field feeds the mobile app, so in practice it often holds a
      deep link like `livgood://collection/promotions`. See webHref().
    - `collectionId` is the mobile app's field and is never used here.
    - `banner` is already resolved for platform=web.
    - `data` is already sorted by `position`; only live banners are returned.
    - An empty list is a normal state — render nothing, never an error.
*/
if (typeof customElements.get('type-banners') === 'undefined') {

  const LG_TB_CACHE_KEY = 'lg_type_banners_v1';
  const LG_TB_CACHE_TTL = 5 * 60 * 1000; // 5 minutes — banners change rarely

  class TypeBanners extends HTMLElement {

    connectedCallback() {
      this.rail = this.querySelector('[data-tb-rail]');
      this.apiBase = (this.dataset.apiBase || 'https://livgood-pt.herokuapp.com/api/v2/type-banner').replace(/\/+$/, '');
      this.limit = parseInt(this.dataset.limit, 10) || 6;
      this.matchAll = this.dataset.matchAll === 'true';
      this.maxProductTypes = parseInt(this.dataset.maxProductTypes, 10) || 3;
      this.shelfSelector = this.dataset.shelfSelector || '.product-type-row';
      this.aspectRatio = this.dataset.aspectRatio || '3.5';

      this.candidates = this.buildCandidates();
      // Shelves are decorated even when the collection itself matches nothing,
      // so an empty candidate list is not a reason to stop.
      if (!this.rail) return;

      this.load();
    }

    /* Ordered, de-duplicated list of names this collection may be known by. */
    buildCandidates() {
      const list = [];
      const push = (value) => {
        const v = String(value || '').trim();
        if (v) list.push(v);
      };

      push(this.dataset.typeOverride);
      push(this.dataset.metafieldType);

      let productTypes = [];
      try {
        productTypes = JSON.parse(this.dataset.productTypes || '[]');
      } catch (e) {
        productTypes = [];
      }
      // Only trust the product types when the collection is narrow enough to
      // genuinely be one type's shelf.
      if (Array.isArray(productTypes) && productTypes.length && productTypes.length <= this.maxProductTypes) {
        productTypes.forEach(push);
      }

      push(this.dataset.collectionHandle);
      push(this.dataset.collectionTitle);

      const seen = {};
      return list.filter((v) => {
        const k = TypeBanners.normalize(v);
        if (!k || seen[k]) return false;
        seen[k] = true;
        return true;
      });
    }

    async load() {
      try {
        const banners = await this.fetchBanners();
        if (!banners.length) return;

        const index = this.indexByType(banners);
        const used = {};

        // Pass one — the shelves. This theme's collection grid splits products
        // into one row per product type, each with its own heading. A banner
        // for "Protein Bars" belongs above that shelf, which is how it reaches
        // a mixed collection like /collections/snacks: the collection itself
        // is not a type, but the shelves inside it are.
        this.decorateTypeShelves(index, used);

        // Pass two — the collection as a whole, for a single-type collection
        // page like /collections/protein-bars. Banners already placed on a
        // shelf are skipped, so the same artwork never appears twice.
        const matched = [];
        for (let i = 0; i < this.candidates.length; i++) {
          const hits = this.lookup(index, this.candidates[i]);
          if (!hits.length) continue;

          let fresh = false;
          hits.forEach((banner) => {
            const key = TypeBanners.keyOf(banner);
            if (used[key]) return;
            used[key] = true;
            matched.push(banner);
            fresh = true;
          });

          if (fresh && !this.matchAll) break; // first matching name wins
        }

        if (!matched.length) return;
        this.render(matched.slice(0, this.limit));
      } catch (e) {
        // Fail open — a banner campaign never blocks the collection page.
        console.warn('[type-banners]', e);
      }
    }

    static keyOf(banner) {
      return banner.id || (banner.type + '|' + banner.banner);
    }

    /*
      Put each banner above the product shelf for its type.

      sections/main-collection-product-grid.liquid renders one .product-type-row
      per product type, with the type's name in .product-type-title and the
      products in .product-type-grid. The name is read from the title's own
      text, so this keeps working whether the theme renders it as a span or a
      link.
    */
    decorateTypeShelves(index, used) {
      const shelves = document.querySelectorAll(this.shelfSelector);
      if (!shelves.length) return;

      shelves.forEach((shelf) => {
        if (shelf.querySelector('[data-tb-shelf-banner]')) return; // already done

        const titleNode = shelf.querySelector('.product-type-title');
        const grid = shelf.querySelector('.product-type-grid');
        if (!titleNode || !grid) return;

        // The heading holds the type name plus a "View All" link; drop the
        // link's text so only the type name is matched on.
        const label = TypeBanners.shelfLabel(titleNode);
        const hits = this.lookup(index, label);
        if (!hits.length) return;

        const banner = hits.filter((b) => !used[TypeBanners.keyOf(b)])[0] || hits[0];
        used[TypeBanners.keyOf(banner)] = true;

        const card = this.buildCard(banner);
        if (!card) return;

        const holder = document.createElement('div');
        holder.className = 'type-banners type-banners--shelf tb-shelf';
        holder.setAttribute('data-tb-shelf-banner', '');
        holder.style.setProperty('--tb-aspect-ratio', this.aspectRatio);
        holder.appendChild(card);

        grid.parentNode.insertBefore(holder, grid);
      });
    }

    static shelfLabel(titleNode) {
      const clone = titleNode.cloneNode(true);
      const viewAll = clone.querySelector('.view-all');
      if (viewAll) viewAll.remove();
      return clone.textContent.trim();
    }

    async fetchBanners() {
      const cached = this.readCache();
      if (cached) return cached;

      const res = await fetch(this.apiBase + '/active?platform=web', {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) return [];

      const json = await res.json();
      // Business failures come back as HTTP 200 with success:false.
      if (!json || json.success === false) return [];

      const data = Array.isArray(json.data) ? json.data : [];
      this.writeCache(data);
      return data;
    }

    readCache() {
      try {
        const raw = window.sessionStorage.getItem(LG_TB_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.ts && (Date.now() - parsed.ts) < LG_TB_CACHE_TTL && Array.isArray(parsed.data)) {
          return parsed.data;
        }
      } catch (e) { /* ignore */ }
      return null;
    }

    writeCache(data) {
      try {
        window.sessionStorage.setItem(LG_TB_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
      } catch (e) { /* storage full / disabled — ignore */ }
    }

    /* Key every banner by both its type and its display label. */
    indexByType(banners) {
      const index = {};
      const add = (key, banner) => {
        const k = TypeBanners.normalize(key);
        if (!k) return;
        if (!index[k]) index[k] = [];
        if (index[k].indexOf(banner) === -1) index[k].push(banner);
      };

      banners.forEach((banner) => {
        add(banner.type, banner);
        add(banner.typeLabel, banner);
      });
      return index;
    }

    lookup(index, candidate) {
      return index[TypeBanners.normalize(candidate)] || [];
    }

    /*
      A browser-usable href for a banner.

      One admin sets the destination for both clients, so `link` is whatever
      the app needed — in practice it is often a mobile deep link such as
      `livgood://collection/promotions`, which does nothing in a browser and
      shows as a broken link. So only a real web url is used as-is; anything
      else is turned back into a Shopify collection url, preferring
      `collectionHandle` and falling back to the handle carried in the deep
      link's last segment.

      Returns '' when there is no usable destination, which makes the banner
      render as a plain image even if `tappable` is true — better than a link
      that goes nowhere.
    */
    webHref(banner) {
      const link = String(banner.link || '').trim();
      if (/^https?:\/\//i.test(link) || link.charAt(0) === '/') return link;

      let handle = String(banner.collectionHandle || '').trim();
      if (!handle && link) {
        const deepLink = link.match(/^[a-z][a-z0-9+.\-]*:\/\/collections?\/([^/?#]+)/i);
        if (deepLink) handle = decodeURIComponent(deepLink[1]);
      }
      return handle ? '/collections/' + handle : '';
    }

    /* "Snacks & Bars" -> "snacks-bars"; "home_top" -> "home-top". */
    static normalize(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    /* One banner card. Shared by the section's own rail and the type shelves. */
    buildCard(banner) {
      const image = (banner.banner || '').trim();
      if (!image) return null;

      const href = this.webHref(banner);
      const isLink = banner.tappable === true && href !== '';

      const card = document.createElement(isLink ? 'a' : 'div');
      card.className = 'tb-card element--border-radius';
      if (isLink) {
        card.href = href;
        card.setAttribute('aria-label', banner.title || banner.collectionTitle || banner.typeLabel || '');
      }

      const imgWrap = document.createElement('span');
      imgWrap.className = 'tb-card__image';

      const img = document.createElement('img');
      img.src = image;
      img.alt = banner.title || banner.typeLabel || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      imgWrap.appendChild(img);
      card.appendChild(imgWrap);

      const title = (banner.title || '').trim();
      const subtitle = (banner.subtitle || '').trim();

      if (title || subtitle) {
        const text = document.createElement('span');
        text.className = 'tb-card__text';

        if (title) {
          const t = document.createElement('span');
          t.className = 'tb-card__title';
          t.textContent = title;
          text.appendChild(t);
        }
        if (subtitle) {
          const sub = document.createElement('span');
          sub.className = 'tb-card__subtitle';
          sub.textContent = subtitle;
          text.appendChild(sub);
        }
        card.appendChild(text);
      }

      return card;
    }

    render(banners) {
      const frag = document.createDocumentFragment();
      let rendered = 0;
      let label = '';

      banners.forEach((banner) => {
        const card = this.buildCard(banner);
        if (!card) return;

        if (!label) label = banner.typeLabel || '';
        frag.appendChild(card);
        rendered += 1;
      });

      if (!rendered) return;

      // typeLabel is the display name — use it in copy when the section has a
      // heading placeholder for it.
      const headingSlot = this.querySelector('[data-tb-type-label]');
      if (headingSlot) {
        if (label) headingSlot.textContent = label;
        else headingSlot.remove();
      }

      this.rail.innerHTML = '';
      this.rail.appendChild(frag);
      this.classList.toggle('type-banners--single', rendered === 1);
      this.removeAttribute('hidden');
    }
  }

  customElements.define('type-banners', TypeBanners);
}
